// src/components/OpenSessionsScreen.jsx
// Painel operacional com todas as sessões que já deveriam ter acontecido
// e ainda não foram encerradas.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Menu,
  RefreshCw,
  Search,
  TimerReset,
  UserCheck,
  Video,
  MapPin,
  X,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";

import "../sass/pages/openSessions.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const FINAL_STATUS = new Set([
  "baixado",
  "cancelado",
  "expirado",
  "no_show",
]);

const AUTH_FAILURE_CODES = new Set([
  "TOKEN_EXPIRED",
  "TOKEN_INVALID",
  "TOKEN_NOT_FOUND",
  "UNAUTHORIZED",
  "AUTH_REQUIRED",
  "AUTHENTICATION_REQUIRED",
  "INVALID_TOKEN",
  "JWT_EXPIRED",
]);

function readAuth(key) {
  return (
    sessionStorage.getItem(key) ||
    localStorage.getItem(key) ||
    ""
  );
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(
    AUTH_KEYS.TOKEN,
    token
  );

  sessionStorage.setItem(
    AUTH_KEYS.TOKEN,
    token
  );
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .toLowerCase()
    .trim();
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function toISODate(date) {
  if (
    !(date instanceof Date) ||
    Number.isNaN(date.getTime())
  ) {
    return "";
  }

  return `${date.getFullYear()}-${pad2(
    date.getMonth() + 1
  )}-${pad2(date.getDate())}`;
}

function startOfToday() {
  const now = new Date();

  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
}

function parseApiDate(item) {
  const raw =
    item?.dataHora ||
    item?.data;

  if (!raw) {
    return null;
  }

  const date =
    new Date(raw);

  return Number.isNaN(
    date.getTime()
  )
    ? null
    : date;
}

function formatDateBR(date) {
  if (
    !(date instanceof Date) ||
    Number.isNaN(date.getTime())
  ) {
    return "--/--/----";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }
  ).format(date);
}

// function formatLongDateBR(date) {
//   if (
//     !(date instanceof Date) ||
//     Number.isNaN(date.getTime())
//   ) {
//     return "";
//   }

//   return new Intl.DateTimeFormat(
//     "pt-BR",
//     {
//       weekday: "long",
//       day: "2-digit",
//       month: "long",
//       year: "numeric",
//     }
//   ).format(date);
// }

function formatHHMM(date) {
  if (
    !(date instanceof Date) ||
    Number.isNaN(date.getTime())
  ) {
    return "--:--";
  }

  return `${pad2(
    date.getHours()
  )}:${pad2(
    date.getMinutes()
  )}`;
}

function getClientName(
  appointment
) {
  return (
    appointment
      ?.clienteId?.nome ||
    appointment
      ?.cliente?.nome ||
    appointment
      ?.nomeCliente ||
    "Cliente"
  );
}

function getServiceName(
  appointment
) {
  return String(
    appointment?.servico ||
      appointment
        ?.tipoSessao ||
      ""
  ).trim();
}

function getAppointmentStatus(
  appointment
) {
  return String(
    appointment?.status ||
      "pendente"
  )
    .trim()
    .toLowerCase();
}

function isOpenAppointment(
  appointment
) {
  return !FINAL_STATUS.has(
    getAppointmentStatus(
      appointment
    )
  );
}

function isDueAppointment(
  appointment,
  now = new Date()
) {
  const date =
    parseApiDate(appointment);

  return (
    Boolean(date) &&
    date.getTime() <=
      now.getTime() &&
    isOpenAppointment(
      appointment
    )
  );
}

function getClientConfirmationState(
  appointment = {}
) {
  const resposta =
    String(
      appointment
        ?.clienteRespostaConfirmacao ||
        ""
    )
      .trim()
      .toUpperCase();

  const confirmado =
    appointment
      ?.confirmadoPeloCliente ===
      true ||
    resposta === "CONFIRMADO";

  const naoComparecera =
    resposta ===
    "NAO_COMPARECEREI";

  return {
    confirmado,
    naoComparecera,
  };
}

function getMode(
  appointment
) {
  const service =
    getServiceName(
      appointment
    ).toLowerCase();

  if (
    service.includes(
      "online"
    )
  ) {
    return {
      label: "Online",
      tone: "blue",
      icon: Video,
    };
  }

  return {
    label: "Presencial",
    tone: "green",
    icon: MapPin,
  };
}

function getDelayInfo(
  appointment,
  now = new Date()
) {
  const date =
    parseApiDate(
      appointment
    );

  if (!date) {
    return {
      days: 0,
      label:
        "Data não identificada",
      tone: "neutral",
    };
  }

  const today =
    startOfToday();

  const appointmentDay =
    new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

  const diffDays =
    Math.max(
      0,
      Math.floor(
        (
          today.getTime() -
          appointmentDay.getTime()
        ) /
          86400000
      )
    );

  if (diffDays > 0) {
    return {
      days: diffDays,
      label:
        diffDays === 1
          ? "Atrasada há 1 dia"
          : `Atrasada há ${diffDays} dias`,
      tone: "danger",
    };
  }

  if (
    date.getTime() <=
    now.getTime()
  ) {
    return {
      days: 0,
      label: "Pendente hoje",
      tone: "warning",
    };
  }

  return {
    days: 0,
    label: "Próxima",
    tone: "info",
  };
}

function isAuthenticationFailure(
  response,
  payload = {}
) {
  if (
    response?.status ===
    401
  ) {
    return true;
  }

  if (
    response?.status !==
    403
  ) {
    return false;
  }

  const code =
    String(
      payload?.code ||
        payload?.errorCode ||
        payload?.error ||
        ""
    )
      .trim()
      .toUpperCase();

  return (
    AUTH_FAILURE_CODES.has(
      code
    )
  );
}

function getApiErrorMessage(
  payload,
  fallback
) {
  return (
    payload?.message ||
    payload?.detail ||
    payload?.error ||
    fallback ||
    "Não foi possível concluir a operação."
  );
}

function EmptyState({
  hasAppointments,
  filter,
  onClear,
}) {
  return (
    <section className="ops-empty">
      <span
        className="ops-empty__icon"
        aria-hidden="true"
      >
        <CheckCircle2
          size={30}
        />
      </span>

      <strong>
        {hasAppointments
          ? "Nenhuma sessão encontrada neste filtro."
          : "Nenhuma sessão pendente de encerramento."}
      </strong>

      <p>
        {hasAppointments
          ? "Ajuste a busca ou exiba todas as pendências."
          : "Quando um atendimento já tiver acontecido e ainda estiver em aberto, ele aparecerá aqui automaticamente."}
      </p>

      {filter !== "todas" ? (
        <button
          type="button"
          className="ops-btn ops-btn--ghost"
          onClick={onClear}
        >
          Exibir todas
        </button>
      ) : null}
    </section>
  );
}

export default function OpenSessionsScreen() {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const toast =
    useToast();

  const [
    sidebarOpen,
    setSidebarOpen,
  ] = useState(false);

  const [
    appointments,
    setAppointments,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    query,
    setQuery,
  ] = useState("");

  const [
    filter,
    setFilter,
  ] = useState("todas");

  const [
    lastUpdatedAt,
    setLastUpdatedAt,
  ] = useState(null);

  const userName =
    useMemo(
      () =>
        readAuth(
          AUTH_KEYS.USER_NAME
        ) || "",
      []
    );

  const authOrRedirect =
    useCallback(() => {
      const token =
        readAuth(
          AUTH_KEYS.TOKEN
        );

      const userId =
        readAuth(
          AUTH_KEYS.USER_ID
        );

      if (
        !token ||
        !userId
      ) {
        toast.warning({
          title:
            "Sessão expirada",
          message:
            "Faça login novamente para consultar as sessões em aberto.",
          actionLabel:
            "Ir para login",
          onAction: () =>
            navigate(
              "/login",
              {
                replace:
                  true,
              }
            ),
        });

        return null;
      }

      return {
        token,
      };
    }, [
      navigate,
      toast,
    ]);

  const fetchAppointments =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        const auth =
          authOrRedirect();

        if (!auth) {
          return;
        }

        if (silent) {
          setRefreshing(
            true
          );
        } else {
          setLoading(
            true
          );
        }

        try {
          const response =
            await fetch(
              `${API_BASE}/psy/schedule`,
              {
                method:
                  "GET",
                headers: {
                  Authorization:
                    `Bearer ${auth.token}`,
                  Accept:
                    "application/json",
                },
              }
            );

          saveAuthToken(
            response.headers.get(
              "x-new-token"
            )
          );

          const data =
            await response
              .json()
              .catch(
                () => null
              );

          if (
            isAuthenticationFailure(
              response,
              data
            )
          ) {
            toast.warning({
              title:
                "Sessão expirada",
              message:
                "Faça login novamente para continuar.",
              actionLabel:
                "Ir para login",
              onAction:
                () =>
                  navigate(
                    "/login",
                    {
                      replace:
                        true,
                    }
                  ),
            });

            return;
          }

          if (
            response.status ===
            404
          ) {
            setAppointments(
              []
            );

            setLastUpdatedAt(
              new Date()
            );

            return;
          }

          if (
            !response.ok
          ) {
            toast.error({
              title:
                "Não foi possível carregar as sessões",
              message:
                getApiErrorMessage(
                  data,
                  "Tente atualizar novamente em instantes."
                ),
            });

            return;
          }

          setAppointments(
            Array.isArray(
              data
            )
              ? data
              : []
          );

          setLastUpdatedAt(
            new Date()
          );
        } catch (error) {
          console.error(
            "Erro ao buscar sessões em aberto:",
            error
          );

          toast.warning({
            title:
              "Falha de comunicação",
            message:
              error?.message ||
              "Não foi possível conversar com a API agora.",
          });
        } finally {
          setLoading(
            false
          );

          setRefreshing(
            false
          );
        }
      },
      [
        authOrRedirect,
        navigate,
        toast,
      ]
    );

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    if (
      location.state
        ?.refresh
    ) {
      fetchAppointments({
        silent: true,
      });

      navigate(
        location.pathname,
        {
          replace: true,
          state: {},
        }
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.state,
  ]);

  const dueAppointments =
    useMemo(() => {
      const now =
        new Date();

      return (
        appointments || []
      )
        .filter(
          (appointment) =>
            isDueAppointment(
              appointment,
              now
            )
        )
        .sort(
          (a, b) =>
            (
              parseApiDate(
                a
              )?.getTime() ||
              0
            ) -
            (
              parseApiDate(
                b
              )?.getTime() ||
              0
            )
        );
    }, [
      appointments,
    ]);

  const stats =
    useMemo(() => {
      const now =
        new Date();

      const today =
        startOfToday();

      const atrasadas =
        dueAppointments.filter(
          (appointment) => {
            const date =
              parseApiDate(
                appointment
              );

            return (
              date &&
              date.getTime() <
                today.getTime()
            );
          }
        ).length;

      const hoje =
        dueAppointments.filter(
          (appointment) => {
            const date =
              parseApiDate(
                appointment
              );

            return (
              date &&
              toISODate(
                date
              ) ===
                toISODate(
                  now
                )
            );
          }
        ).length;

      const confirmadas =
        dueAppointments.filter(
          (appointment) =>
            getClientConfirmationState(
              appointment
            ).confirmado
        ).length;

      return {
        total:
          dueAppointments.length,
        atrasadas,
        hoje,
        confirmadas,
      };
    }, [
      dueAppointments,
    ]);

  const filteredAppointments =
    useMemo(() => {
      const q =
        normalizeText(
          query
        );

      const today =
        startOfToday();

      return dueAppointments.filter(
        (appointment) => {
          const date =
            parseApiDate(
              appointment
            );

          const confirmation =
            getClientConfirmationState(
              appointment
            );

          const matchFilter =
            filter ===
              "todas" ||
            (
              filter ===
                "atrasadas" &&
              date &&
              date.getTime() <
                today.getTime()
            ) ||
            (
              filter ===
                "hoje" &&
              date &&
              toISODate(
                date
              ) ===
                toISODate(
                  new Date()
                )
            ) ||
            (
              filter ===
                "confirmadas" &&
              confirmation
                .confirmado
            );

          if (
            !matchFilter
          ) {
            return false;
          }

          if (!q) {
            return true;
          }

          const haystack =
            normalizeText(
              [
                getClientName(
                  appointment
                ),
                getServiceName(
                  appointment
                ),
                appointment
                  ?.observacoes,
                confirmation
                  .confirmado
                  ? "confirmada presença confirmada"
                  : "",
                confirmation
                  .naoComparecera
                  ? "não poderá comparecer"
                  : "",
              ]
                .filter(
                  Boolean
                )
                .join(" ")
            );

          return haystack.includes(
            q
          );
        }
      );
    }, [
      dueAppointments,
      filter,
      query,
    ]);

  const openSessionControl =
    (
      appointment
    ) => {
      navigate(
        "/session-control",
        {
          state: {
            appointment,
            returnTo:
              "/open-sessions",
          },
        }
      );
    };

  const viewOnSchedule =
    (
      appointment
    ) => {
      const date =
        parseApiDate(
          appointment
        );

      navigate(
        "/schedule",
        {
          state: {
            selectedDate:
              toISODate(
                date
              ),
          },
        }
      );
    };

  return (
    <div className="ops-page">
      <div
        className="ops-bg"
        aria-hidden="true"
      >
        <span className="ops-bg__orb ops-bg__orb--one" />
        <span className="ops-bg__orb ops-bg__orb--two" />
        <span className="ops-bg__orb ops-bg__orb--three" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() =>
          setSidebarOpen(
            false
          )
        }
      />

      <header className="ops-topbar">
        <button
          className="ops-icon-btn"
          type="button"
          onClick={() =>
            setSidebarOpen(
              true
            )
          }
          aria-label="Abrir menu"
        >
          <Menu
            size={19}
            aria-hidden="true"
          />
        </button>

        <div className="ops-brand">
          <img
            src={Logo}
            alt="PsyAssist"
          />

          <div>
            <strong>
              Sessões em aberto
            </strong>

            <span>
              Pendências clínicas •{" "}
              {userName ||
                "Usuário"}
            </span>
          </div>
        </div>

        <button
          className="ops-btn ops-btn--top"
          type="button"
          disabled={
            loading ||
            refreshing
          }
          onClick={() =>
            fetchAppointments({
              silent: true,
            })
          }
        >
          <RefreshCw
            size={16}
            className={
              refreshing
                ? "ops-spin"
                : ""
            }
            aria-hidden="true"
          />

          {refreshing
            ? "Atualizando..."
            : "Atualizar"}
        </button>
      </header>

      <main className="ops-main">
        <section className="ops-hero">
          <div>
            <span className="ops-kicker">
              Central de pendências
            </span>

            <h1>
              Sessões em aberto
            </h1>

            <p>
              Encontre em um único lugar
              os atendimentos que já
              deveriam ter acontecido e
              ainda precisam ser
              encerrados. Finalize a
              sessão daqui usando o mesmo
              fluxo clínico da Agenda.
            </p>
          </div>

          <aside className="ops-hero__summary">
            <span>
              <Clock3
                size={15}
                aria-hidden="true"
              />
              Pendências atuais
            </span>

            <strong>
              {stats.total}
            </strong>

            <small>
              {stats.total === 1
                ? "sessão aguardando encerramento"
                : "sessões aguardando encerramento"}
            </small>
          </aside>
        </section>

        <section className="ops-stats">
          <article className="ops-stat ops-stat--primary">
            <span>
              Total em aberto
            </span>

            <strong>
              {stats.total}
            </strong>

            <small>
              precisam de ação
            </small>
          </article>

          <article className="ops-stat ops-stat--danger">
            <span>
              Atrasadas
            </span>

            <strong>
              {stats.atrasadas}
            </strong>

            <small>
              de dias anteriores
            </small>
          </article>

          <article className="ops-stat ops-stat--warning">
            <span>
              Hoje
            </span>

            <strong>
              {stats.hoje}
            </strong>

            <small>
              já chegaram ao horário
            </small>
          </article>

          <article className="ops-stat ops-stat--info">
            <span>
              Confirmadas
            </span>

            <strong>
              {stats.confirmadas}
            </strong>

            <small>
              cliente confirmou presença
            </small>
          </article>
        </section>

        <section className="ops-panel">
          <div className="ops-filter-row">
            <div
              className="ops-quick-filters"
              role="group"
              aria-label="Filtrar sessões em aberto"
            >
              <button
                type="button"
                className={`ops-filter ${
                  filter ===
                  "todas"
                    ? "ops-filter--active"
                    : ""
                }`}
                onClick={() =>
                  setFilter(
                    "todas"
                  )
                }
              >
                Todas
                <strong>
                  {stats.total}
                </strong>
              </button>

              <button
                type="button"
                className={`ops-filter ops-filter--danger ${
                  filter ===
                  "atrasadas"
                    ? "ops-filter--active"
                    : ""
                }`}
                onClick={() =>
                  setFilter(
                    "atrasadas"
                  )
                }
              >
                Atrasadas
                <strong>
                  {stats.atrasadas}
                </strong>
              </button>

              <button
                type="button"
                className={`ops-filter ops-filter--warning ${
                  filter ===
                  "hoje"
                    ? "ops-filter--active"
                    : ""
                }`}
                onClick={() =>
                  setFilter(
                    "hoje"
                  )
                }
              >
                Hoje
                <strong>
                  {stats.hoje}
                </strong>
              </button>

              <button
                type="button"
                className={`ops-filter ops-filter--info ${
                  filter ===
                  "confirmadas"
                    ? "ops-filter--active"
                    : ""
                }`}
                onClick={() =>
                  setFilter(
                    "confirmadas"
                  )
                }
              >
                Confirmadas
                <strong>
                  {stats.confirmadas}
                </strong>
              </button>
            </div>

            <label className="ops-search">
              <Search
                size={18}
                aria-hidden="true"
              />

              <input
                value={query}
                onChange={(
                  event
                ) =>
                  setQuery(
                    event.target.value
                  )
                }
                placeholder="Buscar cliente ou serviço..."
                aria-label="Buscar sessão em aberto"
              />

              {query ? (
                <button
                  type="button"
                  onClick={() =>
                    setQuery("")
                  }
                  aria-label="Limpar busca"
                >
                  <X
                    size={15}
                    aria-hidden="true"
                  />
                </button>
              ) : null}
            </label>
          </div>

          <div className="ops-list-head">
            <div>
              <h2>
                {filter ===
                "atrasadas"
                  ? "Sessões atrasadas"
                  : filter ===
                      "hoje"
                    ? "Pendências de hoje"
                    : filter ===
                        "confirmadas"
                      ? "Presenças confirmadas"
                      : "Todas as pendências"}
              </h2>

              <p>
                {loading
                  ? "Carregando sessões..."
                  : `${filteredAppointments.length} sessão(ões) exibida(s)`}
              </p>
            </div>

            {lastUpdatedAt ? (
              <span className="ops-updated">
                Atualizado às{" "}
                {formatHHMM(
                  lastUpdatedAt
                )}
              </span>
            ) : null}
          </div>

          {loading ? (
            <div className="ops-skeleton-list">
              {Array.from({
                length: 4,
              }).map(
                (_, index) => (
                  <div
                    className="ops-skeleton"
                    key={`ops-skeleton-${index}`}
                  />
                )
              )}
            </div>
          ) : filteredAppointments.length ===
            0 ? (
            <EmptyState
              hasAppointments={
                dueAppointments.length >
                0
              }
              filter={filter}
              onClear={() =>
                setFilter(
                  "todas"
                )
              }
            />
          ) : (
            <div className="ops-list">
              {filteredAppointments.map(
                (
                  appointment
                ) => {
                  const date =
                    parseApiDate(
                      appointment
                    );

                  const confirmation =
                    getClientConfirmationState(
                      appointment
                    );

                  const delay =
                    getDelayInfo(
                      appointment
                    );

                  const mode =
                    getMode(
                      appointment
                    );

                  const ModeIcon =
                    mode.icon;

                  return (
                    <article
                      className={`ops-card ops-card--${delay.tone}`}
                      key={
                        appointment?._id ||
                        `${getClientName(
                          appointment
                        )}-${date?.getTime()}`
                      }
                    >
                      <div className="ops-card__main">
                        <div
                          className={`ops-avatar ops-avatar--${mode.tone}`}
                          aria-hidden="true"
                        >
                          <ModeIcon
                            size={22}
                          />
                        </div>

                        <div className="ops-card__body">
                          <div className="ops-card__title-row">
                            <strong>
                              {getClientName(
                                appointment
                              )}
                            </strong>

                            <span
                              className={`ops-badge ops-badge--${delay.tone}`}
                            >
                              {delay.label}
                            </span>

                            {confirmation.confirmado ? (
                              <span className="ops-badge ops-badge--confirmed">
                                <UserCheck
                                  size={13}
                                  aria-hidden="true"
                                />
                                Presença confirmada
                              </span>
                            ) : null}

                            {confirmation.naoComparecera ? (
                              <span className="ops-badge ops-badge--danger">
                                <AlertTriangle
                                  size={13}
                                  aria-hidden="true"
                                />
                                Não poderá comparecer
                              </span>
                            ) : null}
                          </div>

                          <div className="ops-card__meta">
                            <span>
                              <CalendarDays
                                size={14}
                                aria-hidden="true"
                              />
                              {formatDateBR(
                                date
                              )}
                            </span>

                            <span>
                              <Clock3
                                size={14}
                                aria-hidden="true"
                              />
                              {formatHHMM(
                                date
                              )}
                            </span>

                            <span>
                              <ModeIcon
                                size={14}
                                aria-hidden="true"
                              />
                              {mode.label}
                            </span>

                            <span>
                              {getServiceName(
                                appointment
                              ) ||
                                "Serviço não informado"}
                            </span>
                          </div>

                          <p className="ops-card__hint">
                            {confirmation.naoComparecera
                              ? "O cliente informou que não poderá comparecer. Revise o atendimento antes de encerrar."
                              : confirmation.confirmado
                                ? "O cliente confirmou a presença, mas esta sessão ainda não foi encerrada pelo psicólogo."
                                : delay.tone ===
                                    "danger"
                                  ? "Este atendimento permanece em aberto desde uma data anterior."
                                  : "O horário do atendimento já chegou e a sessão ainda precisa ser encerrada."}
                          </p>
                        </div>
                      </div>

                      <div className="ops-card__actions">
                        <button
                          type="button"
                          className="ops-btn ops-btn--ghost"
                          onClick={() =>
                            viewOnSchedule(
                              appointment
                            )
                          }
                        >
                          <CalendarDays
                            size={15}
                            aria-hidden="true"
                          />
                          Ver na agenda
                        </button>

                        <button
                          type="button"
                          className="ops-btn ops-btn--primary"
                          onClick={() =>
                            openSessionControl(
                              appointment
                            )
                          }
                        >
                          <TimerReset
                            size={16}
                            aria-hidden="true"
                          />
                          Encerrar sessão
                        </button>
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
